import { useState } from "react";
import clsx from "clsx";
import { CircleHelpIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  ContactUsDialog,
  DashboardActiveJobs,
  DashboardPipelineActivity,
  JobsEmptyState,
  TopbarContainer,
  TopbarTitle,
} from "@/components";

import {
  useGetPlacementCount,
  useGetTotalMatchedCandidatesForActiveJobs,
} from "@/hooks/queries";
import { storage } from "@/lib/storage";
import { jobsStore } from "@/stores";
import { JobStatus } from "@/types";

const computeGreeting = () => {
  const hours = new Date().getHours();
  // 5am - 11:59am
  if (hours >= 5 && hours < 12) {
    return "Good morning";
    // 12pm - 6:59pm
  } else if (hours >= 12 && hours < 19) {
    return "Good afternoon";
    // 7pm - 4:59am
  } else {
    return "Good evening";
  }
};

const Metric = ({
  className,
  label,
  value,
}: {
  className?: string;
  label: string;
  value: string;
}) => {
  return (
    <div
      className={clsx(
        "flex flex-col gap-1 rounded-lg border border-primary-400 bg-primary-100 p-6",
        className,
      )}
    >
      <p className="text-4xl font-semibold text-slate-900">{value}</p>
      <p className="font-medium text-slate-700">{label}</p>
    </div>
  );
};

export const DashboardPage = () => {
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const { data: placementCount } = useGetPlacementCount();
  const { data: totalMatchedCandidatesForActiveJobs } =
    useGetTotalMatchedCandidatesForActiveJobs();
  const isLoading = jobsStore((state) => state.isLoading);
  // TODO: This is a common pattern so we should create a reusable piece of logic.
  const jobs = jobsStore((state) => state.jobs);
  const activeJobs = jobs.filter(({ job }) => job.status === JobStatus.ACTIVE);
  const currentYearString = new Date().getFullYear().toString();

  // TODO: This is a common pattern so we should create a reusable piece of logic.
  const user = storage.getUser();
  if (!user) {
    throw new Error("User not found in local storage");
  }

  return (
    <>
      <TopbarContainer className="flex items-center justify-between">
        <TopbarTitle>Dashboard</TopbarTitle>
        <Button
          onClick={() => {
            setContactDialogOpen(true);
          }}
          variant="ghost"
        >
          <CircleHelpIcon className="h-4 w-4" />
          Contact us
        </Button>
      </TopbarContainer>
      <div className="flex flex-col gap-8 p-8">
        <div className="flex items-center gap-2">
          <p className="text-4xl font-semibold text-slate-900">
            {computeGreeting()}, {user.firstName} 👋
          </p>
        </div>
        {!isLoading && (
          <>
            <div className="flex gap-8">
              <Metric
                className="w-full"
                label="Active job(s)"
                value={activeJobs.length.toString()}
              />
              {/* TODO: Loading state. */}
              <Metric
                className="w-full"
                label={`Hired through Laborup (${currentYearString})`}
                value={placementCount?.toString() ?? "-"}
              />
              {/* TODO: Loading state. */}
              <Metric
                className="w-full"
                label="Matched candidates"
                value={
                  totalMatchedCandidatesForActiveJobs?.totalMatchedCandidates.toString() ??
                  "-"
                }
              />
            </div>
            {activeJobs.length > 0 ? (
              <>
                <div className="flex gap-8">
                  <DashboardPipelineActivity className="min-h-[350px] w-[60%]" />
                  <DashboardActiveJobs className="min-h-[350px] w-[40%]" />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-slate-200 py-24">
                <JobsEmptyState />
              </div>
            )}
          </>
        )}
      </div>
      <ContactUsDialog
        onOpenChange={setContactDialogOpen}
        open={contactDialogOpen}
      />
    </>
  );
};
